import { DayOfWeek, RecurrencePattern } from "@sp-crm/core";
import { Input } from "components/ui/input";
import { Select } from "components/ui/select";
import { UpdateTaskPayload } from "generated/graphql";
import React, { useCallback } from "react";

interface RecurrenceConfigProps {
    recurrencePattern?: RecurrencePattern | null;
    recurrenceInterval?: number | null;
    recurrenceDaysOfWeek?: DayOfWeek[] | null;
    recurrenceDayOfMonth?: number | null;
    onChange: (changes: Partial<UpdateTaskPayload>) => void;
}

const DAYS_OF_WEEK = [
    { value: DayOfWeek.Sunday, label: "Sunday" },
    { value: DayOfWeek.Monday, label: "Monday" },
    { value: DayOfWeek.Tuesday, label: "Tuesday" },
    { value: DayOfWeek.Wednesday, label: "Wednesday" },
    { value: DayOfWeek.Thursday, label: "Thursday" },
    { value: DayOfWeek.Friday, label: "Friday" },
    { value: DayOfWeek.Saturday, label: "Saturday" },
];

export const RecurrenceConfig: React.FC<RecurrenceConfigProps> = ({
    recurrencePattern,
    recurrenceInterval,
    recurrenceDaysOfWeek,
    recurrenceDayOfMonth,
    onChange,
}) => {
    const handlePatternChange = useCallback(
        (pattern: RecurrencePattern) => {
            const changes: Partial<UpdateTaskPayload> = {
                recurrencePattern: pattern,
            };

            if (pattern !== RecurrencePattern.Weekly) {
                changes.recurrenceDaysOfWeek = null;
            }
            if (pattern !== RecurrencePattern.Monthly) {
                changes.recurrenceDayOfMonth = null;
            }
            if (pattern === RecurrencePattern.None) {
                changes.recurrenceInterval = null;
            }
            if (pattern === RecurrencePattern.Daily) {
                changes.recurrenceInterval = 1;
            }
            if (pattern === RecurrencePattern.Weekly) {
                changes.recurrenceInterval = 1;
            }
            if (pattern === RecurrencePattern.Monthly) {
                changes.recurrenceInterval = 1;
                changes.recurrenceDayOfMonth = 1;
            }

            onChange(changes);
        },
        [onChange],
    );

    const handleIntervalChange = useCallback(
        (value: string) => {
            const interval = parseInt(value, 10);
            if (!isNaN(interval) && interval > 0) {
                onChange({ recurrenceInterval: interval });
            }
        },
        [onChange],
    );

    const handleDaysOfWeekChange = useCallback(
        (day: DayOfWeek, checked: boolean) => {
            const currentDays = recurrenceDaysOfWeek || [];
            const newDays = checked
                ? [...currentDays, day]
                : currentDays.filter(d => d !== day);
            onChange({ recurrenceDaysOfWeek: newDays.length > 0 ? newDays : null });
        },
        [recurrenceDaysOfWeek, onChange],
    );

    const handleDayOfMonthChange = useCallback(
        (value: string) => {
            const day = parseInt(value, 10);
            if (!isNaN(day) && day >= 1 && day <= 31) {
                onChange({ recurrenceDayOfMonth: day });
            }
        },
        [onChange],
    );

    const pattern = recurrencePattern || RecurrencePattern.None;

    return (
        <div className="space-y-4">
            <div>
                <Select
                    label="Repeat"
                    value={pattern}
                    onChange={e =>
                        handlePatternChange(e.target.value as RecurrencePattern)
                    }>
                    <option value={RecurrencePattern.None}>None</option>
                    <option value={RecurrencePattern.Daily}>Daily</option>
                    <option value={RecurrencePattern.Weekly}>Weekly</option>
                    <option value={RecurrencePattern.Monthly}>Monthly</option>
                </Select>
            </div>

            {pattern !== RecurrencePattern.None && (
                <>
                    <div>
                        <Input
                            type="number"
                            label={`Recurs every ${
                                (recurrenceInterval || 1) === 1
                                    ? pattern === RecurrencePattern.Daily
                                        ? "day"
                                        : pattern === RecurrencePattern.Weekly
                                        ? "week"
                                        : "month"
                                    : `${recurrenceInterval || 1} ${
                                          pattern === RecurrencePattern.Daily
                                              ? "days"
                                              : pattern === RecurrencePattern.Weekly
                                              ? "weeks"
                                              : "months"
                                      }`
                            }`}
                            value={recurrenceInterval || 1}
                            onChange={e => handleIntervalChange(e.target.value)}
                            min={1}
                        />
                    </div>

                    {pattern === RecurrencePattern.Weekly && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Repeat on
                            </label>
                            <div className="space-y-2">
                                {DAYS_OF_WEEK.map(day => (
                                    <label key={day.value} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            checked={(
                                                recurrenceDaysOfWeek || []
                                            ).includes(day.value)}
                                            onChange={e =>
                                                handleDaysOfWeekChange(
                                                    day.value,
                                                    e.target.checked,
                                                )
                                            }
                                        />
                                        <span className="ml-2 text-sm text-gray-700">
                                            {day.label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {pattern === RecurrencePattern.Monthly && (
                        <div>
                            <Input
                                type="number"
                                label="Day of month"
                                value={recurrenceDayOfMonth || 1}
                                onChange={e => handleDayOfMonthChange(e.target.value)}
                                min={1}
                                max={31}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
